const resources = [
  {
    icon: "ri-government-line",
    title: "U.S. State Department Passport Services",
    desc: "Official information on passport requirements, fees, and application forms.",
    link: "https://travel.state.gov/content/travel/en/passports.html",
    label: "travel.state.gov",
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Find Acceptance Facilities",
    desc: "Locate authorized passport acceptance locations near Sioux Falls.",
    link: "https://iafdb.travel.state.gov/",
    label: "iafdb.travel.state.gov",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Schedule a Passport Appointment",
    desc: "Book an appointment at the Minneapolis Passport Agency for genuine emergencies.",
    link: "https://passportappointment.travel.state.gov/",
    label: "passportappointment.travel.state.gov",
  },
  {
    icon: "ri-search-line",
    title: "Check Application Status",
    desc: "Track the status of your pending U.S. passport application online.",
    link: "https://passportstatus.state.gov/",
    label: "passportstatus.state.gov",
  },
  {
    icon: "ri-file-list-3-line",
    title: "DS-11 Application (New Passport)",
    desc: "Download the official DS-11 form for first-time U.S. passport applicants.",
    link: "https://eforms.state.gov/Forms/ds11.pdf",
    label: "eforms.state.gov",
  },
  {
    icon: "ri-file-list-line",
    title: "DS-82 Application (Renewal)",
    desc: "Download the official DS-82 form for eligible adult passport renewals.",
    link: "https://eforms.state.gov/Forms/ds82.pdf",
    label: "eforms.state.gov",
  },
];

const vitalRecords = [
  {
    icon: "ri-user-heart-line",
    label: "Birth Certificates",
    desc: "South Dakota Department of Health — Vital Records. Order online, by mail, or in person at the Minnehaha County office.",
    url: "https://doh.sd.gov/records/vital-records/",
  },
  {
    icon: "ri-heart-pulse-line",
    label: "Death Certificates",
    desc: "Certified copies available through SD Vital Records. Required for some passport applications.",
    url: "https://doh.sd.gov/records/vital-records/",
  },
  {
    icon: "ri-links-line",
    label: "Marriage Certificates",
    desc: "Minnehaha County Register of Deeds or SD Vital Records. Needed for passport name change after marriage.",
    url: "https://doh.sd.gov/records/vital-records/",
  },
  {
    icon: "ri-scales-3-line",
    label: "Divorce Decrees",
    desc: "Minnehaha County Clerk of Courts. Contact the Clerk for certified copies of divorce records.",
    url: "https://ujs.sd.gov/",
  },
];

export default function HelpfulResourcesSection() {
  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Passport Resources for Sioux Falls Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base">
            Official government links and resources for Sioux Falls and South Dakota passport applicants.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {resources.map((r, i) => (
            <a
              key={i}
              href={r.link}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="group bg-white rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-3">
                <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-1.5">{r.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed mb-3 flex-1">{r.desc}</p>
              <span className="text-[#F6C344] text-xs font-semibold group-hover:underline">{r.label} &rarr;</span>
            </a>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-10">
          <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
              <i className="ri-file-text-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
              <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Sioux Falls</p>
            </div>
          </div>
          <div className="px-6 py-6">
            <p className="text-gray-500 text-sm leading-relaxed mb-5">
              If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records for Sioux Falls residents.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              {vitalRecords.map((record, i) => (
                <a key={i} href={record.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                    <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                    <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors"><i className="ri-external-link-line text-xs"></i>Get Records</span>
                  </div>
                </a>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-gray-600 text-xs leading-relaxed">
                <i className="ri-information-line text-amber-500 mr-1.5"></i>
                <strong>Don&apos;t see your county?</strong> Visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-white rounded-lg border border-gray-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="font-bold text-[#1A365D] text-base mb-2">Other South Dakota Cities We Serve</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: "Rapid City", href: "/cities/rapid-city" },
                { name: "Aberdeen SD", href: "/cities/aberdeen-sd" },
                { name: "South Dakota Hub", href: "/states/south-dakota" },
                { name: "Fargo ND", href: "/cities/fargo" },
                { name: "Bismarck ND", href: "/cities/bismarck" },
                { name: "Billings MT", href: "/cities/billings" },
              ].map((city, i) => (
                <a
                  key={i}
                  href={city.href}
                  className="text-xs font-semibold text-[#1A365D] bg-[#EEF2F8] hover:bg-[#F6C344] hover:text-[#1A365D] px-3 py-1.5 rounded-full transition-all whitespace-nowrap"
                >
                  {city.name}
                </a>
              ))}
            </div>
          </div>
          <div className="text-center md:text-right">
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap"
            >
              Start My Passport Now <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}